import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Radio, RefreshCw, Link2 } from "lucide-react";

export default function LiveScoresPanel({ competitionId, gameweekId, fixtures, onFixturesChanged }) {
  const [configured, setConfigured] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [msgTone, setMsgTone] = useState("info"); // info | success | error
  const [pl, setPl] = useState([]);
  const [linkingFx, setLinkingFx] = useState(null);

  useEffect(() => {
    api.get("/live-scores/status").then(({ data }) => setConfigured(!!data.configured));
  }, []);

  const sync = async () => {
    setBusy(true); setMsg("");
    try {
      const { data } = await api.post(`/competitions/${competitionId}/live-sync`);
      if (data.ok === false) {
        setMsgTone("error");
        setMsg(data.error || "Sync failed");
      } else {
        setMsgTone("success");
        setMsg(`Synced. Updated ${data.updates ?? 0} of ${data.checked ?? 0} fixtures.`);
        onFixturesChanged?.();
      }
    } catch (e) {
      setMsgTone("error");
      setMsg(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const openLinkModal = async (fx) => {
    setLinkingFx(fx);
    setPl([]);
    try {
      const { data } = await api.get(`/live-scores/premier-league/today`);
      setPl(data.matches || []);
    } catch { /* ignore */ }
  };

  const linkFixture = async (externalId) => {
    if (!linkingFx) return;
    setBusy(true);
    try {
      await api.post(`/fixtures/${linkingFx.id}/link-external`, { external_match_id: externalId });
      setLinkingFx(null);
      onFixturesChanged?.();
      setMsgTone("success");
      setMsg(externalId ? "Fixture linked. Live scores will flow in." : "Fixture unlinked.");
    } catch (e) {
      setMsgTone("error");
      setMsg(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (configured === null) return null;

  return (
    <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]" data-testid="live-scores-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <div className="flex items-center gap-2">
          <Radio className="w-5 h-5 text-red-400" />
          <h2 className="font-heading text-lg font-semibold">Live Scores</h2>
          {!configured && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F5B942]/15 text-[#F5B942]">Not configured</span>}
        </div>
        {configured && (
          <button onClick={sync} disabled={busy} data-testid="live-sync-btn"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-[#132A45] hover:bg-[#1B4276] disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> {busy ? "Syncing…" : "Sync now"}
          </button>
        )}
      </div>

      {!configured ? (
        <p className="text-sm text-[#AAB7C7]">
          Add a free <a href="https://www.football-data.org/client/register" target="_blank" rel="noreferrer" className="text-[#C7F500] hover:underline">football-data.org API key</a> to auto-pull Premier League scores every 60 seconds during live matches.
        </p>
      ) : (
        <>
          <p className="text-xs text-[#AAB7C7]">Premier League scores auto-sync every 60s while a match is live. Points recalc immediately.</p>
          {gameweekId && fixtures?.length > 0 && (
            <div className="mt-3 space-y-1.5">
              {fixtures.map((fx) => (
                <div key={fx.id} className="flex items-center justify-between text-xs bg-[#071426] rounded-lg px-3 py-2">
                  <span className="truncate">{fx.home_team} <span className="text-[#7A8AA3]">v</span> {fx.away_team}</span>
                  <div className="flex items-center gap-2">
                    {fx.external_match_id ? (
                      <span className="text-[#C7F500] inline-flex items-center gap-1"><Link2 className="w-3 h-3" /> linked</span>
                    ) : (
                      <span className="text-[#7A8AA3]">auto-match</span>
                    )}
                    <button onClick={() => openLinkModal(fx)} data-testid={`link-fx-${fx.id}`}
                      className="text-[#C7F500] hover:underline">{fx.external_match_id ? "Change" : "Link"}</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {msg && (
        <div className={`mt-3 text-xs ${msgTone === "error" ? "text-red-400" : msgTone === "success" ? "text-[#C7F500]" : "text-[#AAB7C7]"}`} data-testid="live-scores-msg">{msg}</div>
      )}

      {linkingFx && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setLinkingFx(null)}>
          <div className="bg-[#0D2038] border border-[#1F3A5C] rounded-2xl w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-semibold mb-1">Link fixture to Premier League match</h3>
            <p className="text-xs text-[#AAB7C7] mb-3">{linkingFx.home_team} v {linkingFx.away_team}</p>
            <div className="max-h-80 overflow-y-auto divide-y divide-[#1F3A5C]">
              {pl.length === 0 && <p className="text-sm text-[#AAB7C7]">No upcoming PL matches available.</p>}
              {pl.map((m) => (
                <button key={m.id} onClick={() => linkFixture(m.id)} data-testid={`pl-match-${m.id}`}
                  className="w-full text-left py-2.5 text-sm hover:bg-[#132A45] px-2 rounded">
                  <div className="font-medium">{m.home} <span className="text-[#7A8AA3]">v</span> {m.away}</div>
                  <div className="text-xs text-[#AAB7C7]">{new Date(m.utc_date).toLocaleString([], { weekday:"short", day:"numeric", month:"short", hour:"2-digit", minute:"2-digit" })} · {m.status}</div>
                </button>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-between">
              {linkingFx.external_match_id && (
                <button onClick={() => linkFixture(null)} className="text-xs text-red-400 hover:underline" data-testid="unlink-fx">Unlink</button>
              )}
              <button onClick={() => setLinkingFx(null)} className="ml-auto text-xs px-3 py-1.5 rounded bg-[#132A45] hover:bg-[#1B4276]">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
